/**
 * Achievement definitions (110 total).
 *
 * Each def has a `getValue(stats)` that returns the current progress number,
 * and `tiers[]` with ascending thresholds. The AchievementSystem compares
 * value → tiers to determine unlock level.
 *
 * For min-tracker achievements (lower is better, e.g. fastest map clear),
 * the def sets `minTracker: true` and the comparison inverts: a tier is
 * unlocked when value <= threshold (and value > 0 to exclude unset state).
 */

import type { PersistentStats, WeaponCategory } from './PersistentStats';

export type AchievementCategory =
  | 'Combat' | 'Progression' | 'Exploration' | 'Loot' | 'Challenge' | 'Meta' | 'Misc';

export interface AchievementDef {
  id: string;
  name: string;
  description: string;            // may use {X} for next-tier threshold
  tiers: number[];                // ascending thresholds
  tierNames?: string[];
  category: AchievementCategory;
  hidden?: boolean;
  minTracker?: boolean;           // true = lower is better (speedruns)
  getValue: (stats: PersistentStats) => number;
  formatValue?: (v: number) => string;
}

const BRONZE_DIAMOND = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];
const GOLD_DIAMOND = ['Gold', 'Platinum', 'Diamond'];

function sumCategory(r: Record<WeaponCategory, number>): number {
  return r.ballistic + r.energy + r.rocket + r.bomb + r.melee;
}

function formatDuration(sec: number): string {
  if (sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = Math.floor(sec % 60);
  return `${m}m ${s}s`;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  // ══════════════════════════════════════════════════════
  // COMBAT (30)
  // ══════════════════════════════════════════════════════
  { id: 'combat_kills_total', name: 'Scrap Tally',
    description: 'Defeat {X} hostiles across all deployments',
    tiers: [50, 200, 1000, 5000, 25000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.totalKills },
  { id: 'combat_kills_per_run', name: 'Field Day',
    description: 'Defeat {X} hostiles in a single deployment',
    tiers: [25, 100, 400, 1500, 5000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.bestRunKills },
  { id: 'combat_swarm_kills', name: 'Pest Control',
    description: 'Crush {X} swarm units',
    tiers: [200, 1000, 5000, 25000, 100000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.swarmKills },
  { id: 'combat_swarm_concurrent', name: 'Locust Plague',
    description: 'Defeat {X} swarm units within a 10-second window',
    tiers: [50, 150, 400], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.bestSwarmBurst10s },
  { id: 'combat_swarm_t5', name: 'Apex Swatter',
    description: 'Defeat {X} T5 swarm units',
    tiers: [10, 50, 250], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.swarmKillsByTier[5] },
  { id: 'combat_kills_turret', name: 'Decommissioner',
    description: 'Destroy {X} turrets',
    tiers: [25, 100, 500, 2000, 8000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByType.turret },
  { id: 'combat_kills_patrol', name: 'Sentry Silencer',
    description: 'Eliminate {X} patrols',
    tiers: [25, 100, 500, 2000, 8000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByType.patrol },
  { id: 'combat_kills_charger', name: 'Matador',
    description: 'Stop {X} chargers',
    tiers: [10, 50, 250, 1000, 4000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByType.charger },
  { id: 'combat_kills_bomber', name: 'Defuser',
    description: 'Detonate {X} bombers before impact',
    tiers: [10, 50, 250, 1000, 4000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByType.bomber },
  { id: 'combat_kills_shielder', name: 'Shield Breaker',
    description: 'Crack {X} shielders',
    tiers: [10, 50, 250, 1000, 4000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByType.shielder },
  { id: 'combat_boss_first', name: 'Giant Slayer',
    description: 'Defeat your first boss',
    tiers: [1], category: 'Combat', getValue: (s) => s.bossKills },
  { id: 'combat_boss_total', name: 'Throne Toppler',
    description: 'Defeat {X} bosses',
    tiers: [3, 10, 25, 75, 200], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.bossKills },
  { id: 'combat_boss_flawless', name: 'Untouchable Operator',
    description: 'Defeat {X} bosses without taking damage',
    tiers: [1, 5, 20], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.flawlessBossKills },
  { id: 'combat_boss_speedrun', name: 'Hostile Takeover',
    description: 'Defeat a boss in under 30 seconds',
    tiers: [1], category: 'Combat', getValue: (s) => s.bossSpeedrun30 },
  { id: 'combat_boss_phases', name: 'Phase Shift',
    description: 'Complete {X} boss phase transitions',
    tiers: [5, 25, 100], category: 'Combat', getValue: (s) => s.bossPhasesCleared },
  { id: 'combat_burst_kills', name: 'Overclocked',
    description: 'Defeat {X} enemies within 5 seconds',
    tiers: [10, 25, 60], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.bestKillBurst5s },
  { id: 'combat_no_damage_streak', name: 'Pristine Chassis',
    description: 'Defeat {X} enemies in a row without taking damage',
    tiers: [25, 100, 500], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.bestNoDamageKillStreak },
  { id: 'combat_no_damage_run', name: 'Ghost in the Machine',
    description: 'Complete a deployment without taking any damage',
    tiers: [1], category: 'Combat', getValue: (s) => s.flawlessRun },
  { id: 'combat_weapon_ballistic', name: 'Lead Symphony',
    description: 'Score {X} kills with ballistic weapons',
    tiers: [100, 500, 2500, 10000, 40000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByCategory.ballistic },
  { id: 'combat_weapon_energy', name: 'Photon Maestro',
    description: 'Score {X} kills with energy weapons',
    tiers: [100, 500, 2500, 10000, 40000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByCategory.energy },
  { id: 'combat_weapon_rocket', name: 'Skyfall Engineer',
    description: 'Score {X} kills with rockets',
    tiers: [50, 250, 1000, 5000, 20000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByCategory.rocket },
  { id: 'combat_weapon_bomb', name: 'Demolition Permit',
    description: 'Score {X} kills with bombs',
    tiers: [50, 250, 1000, 5000, 20000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByCategory.bomb },
  { id: 'combat_weapon_melee', name: 'Up Close and Personal',
    description: 'Score {X} kills with melee weapons',
    tiers: [25, 150, 750, 3000, 12000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByCategory.melee },
  { id: 'combat_melee_only_run', name: 'Knife to a Gunfight',
    description: 'Complete a deployment using only melee kills',
    tiers: [1], category: 'Combat', getValue: (s) => s.meleeOnlyRun },
  { id: 'combat_mythic_kills', name: 'Forge-Forged',
    description: 'Score {X} kills using a tier-10 weapon',
    tiers: [50, 250, 1000], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.killsByWeaponTier[10] ?? 0 },
  { id: 'combat_all_categories_run', name: 'Full Arsenal',
    description: 'Score a kill with every weapon category in a single deployment',
    tiers: [1], category: 'Combat', getValue: (s) => s.allCategoriesRun },
  { id: 'combat_damage_total', name: 'Joules Delivered',
    description: 'Deal {X} total damage across all deployments',
    tiers: [10000, 100000, 1000000, 10000000, 100000000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.totalDamageDealt },
  { id: 'combat_single_hit', name: 'Hammer of the Operator',
    description: 'Deal {X} damage in a single hit',
    tiers: [500, 5000, 50000], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.biggestHit },
  { id: 'combat_crit_total', name: 'Weak-Spot Specialist',
    description: 'Land {X} critical hits',
    tiers: [100, 1000, 10000, 50000, 250000], tierNames: BRONZE_DIAMOND,
    category: 'Combat', getValue: (s) => s.totalCrits },
  { id: 'combat_burn_kills', name: 'Cooked to Spec',
    description: 'Defeat {X} enemies via burn damage',
    tiers: [50, 250, 1500, 7500],
    category: 'Combat', getValue: (s) => s.killsByDoT.burn },
  { id: 'combat_stun_kills', name: 'Lights Out',
    description: 'Defeat {X} stunned enemies',
    tiers: [25, 150, 750, 3000],
    category: 'Combat', getValue: (s) => s.killsWhileStunned },
  { id: 'combat_chain_kills', name: 'Domino Protocol',
    description: 'Kill {X} enemies with a single splash or pierce hit',
    tiers: [3, 6, 12], tierNames: GOLD_DIAMOND,
    category: 'Combat', getValue: (s) => s.bestChainKill },
  { id: 'combat_knockback_kills', name: "Newton's Apology",
    description: 'Defeat {X} enemies while airborne from knockback',
    tiers: [25, 150, 750], category: 'Combat', getValue: (s) => s.knockbackKills },

  // ══════════════════════════════════════════════════════
  // PROGRESSION / EXPLORATION (27)
  // ══════════════════════════════════════════════════════
  // Note: level/stat values come from StatsSystem, not PersistentStats.
  // These getValue fns reach into the live systems via a shim at check-time.
  // For Phase 1, they return 0; Phase 3 will wire a context reference.
  { id: 'progress_deployments', name: 'Field Tested',
    description: 'Complete {X} deployments',
    tiers: [1, 10, 50, 200, 1000], tierNames: BRONZE_DIAMOND,
    category: 'Progression', getValue: (s) => s.runsCompleted },
  { id: 'progress_first_recall', name: 'First Light',
    description: 'Survive your first deployment and return to base',
    tiers: [1], category: 'Progression', getValue: (s) => s.runsRecalled },
  { id: 'progress_perfect_run', name: "Cartographer's Pride",
    description: 'Complete a deployment with 100% map exploration',
    tiers: [1, 5, 25], category: 'Exploration', getValue: (s) => s.perfectRunCount },
  { id: 'progress_explore_percent', name: 'Mapmaker',
    description: 'Reach {X}% map exploration in a single run',
    tiers: [25, 50, 75, 100],
    category: 'Exploration', getValue: (s) => s.bestExplorationPercent },
  { id: 'progress_largest_map_explored', name: 'Sprawl Walker',
    description: 'Fully explore a map of at least {X} tiles',
    tiers: [500, 2000, 8000],
    category: 'Exploration', getValue: (s) => s.maxMapFullyExplored },
  { id: 'progress_tiles_revealed_total', name: 'Lifting the Fog',
    description: 'Reveal {X} tiles across all runs',
    tiers: [1000, 10000, 100000, 500000, 2000000], tierNames: BRONZE_DIAMOND,
    category: 'Exploration', getValue: (s) => s.tilesRevealedTotal },
  { id: 'progress_longest_deployment', name: 'Long Patrol',
    description: 'Stay deployed for {X} seconds in one run',
    tiers: [120, 600, 1800],
    category: 'Exploration', getValue: (s) => s.bestRunDuration,
    formatValue: formatDuration },
  { id: 'progress_distance_from_base', name: 'Far Frontier',
    description: 'Reach a tile at least {X} tiles from the base spawn',
    tiers: [25, 75, 200],
    category: 'Exploration', getValue: (s) => s.bestRunDistanceFromSpawn },
  { id: 'progress_run_xp_record', name: 'Banner Run',
    description: 'Earn {X} XP in a single deployment',
    tiers: [500, 2500, 10000],
    category: 'Progression', getValue: (s) => s.bestRunXp },
  { id: 'progress_buildings_placed', name: 'Builder',
    description: 'Place {X} buildings at the base',
    tiers: [1, 5, 15, 30],
    category: 'Progression', getValue: (s) => s.buildingsPlacedTotal },
  { id: 'progress_money_total', name: 'Industrialist',
    description: 'Earn {X} cumulative credits',
    tiers: [1000, 10000, 100000, 500000, 2000000], tierNames: BRONZE_DIAMOND,
    category: 'Progression', getValue: (s) => s.moneyEarnedTotal },
  { id: 'progress_money_run_record', name: 'Big Score',
    description: 'Earn {X} credits in a single run',
    tiers: [500, 2500, 10000],
    category: 'Progression', getValue: (s) => s.bestRunCoins },
  { id: 'progress_money_spent_trader', name: 'Patron',
    description: 'Spend {X} credits at the Trader',
    tiers: [1000, 10000, 100000],
    category: 'Progression', getValue: (s) => s.moneySpentAtTrader },
  // The following use context-dependent values; Phase 3 wires them via
  // AchievementSystem.setContext({stats, buildings}). Until then, they
  // simply return 0 and remain locked.
  { id: 'progress_level_milestone', name: 'Rising Operator',
    description: 'Reach player level {X}',
    tiers: [5, 10, 25, 50, 100], tierNames: BRONZE_DIAMOND,
    category: 'Progression', getValue: (s) => (s as unknown as { _level?: number })._level ?? 0 },
  { id: 'progress_first_level_10', name: 'Double Digits',
    description: 'Reach player level 10 for the first time',
    tiers: [10], category: 'Progression',
    getValue: (s) => (s as unknown as { _level?: number })._level ?? 0 },
  { id: 'progress_stat_speed', name: 'Quicksilver',
    description: 'Allocate {X} points into Speed',
    tiers: [10, 25, 50], category: 'Progression',
    getValue: (s) => (s as unknown as { _statSpeed?: number })._statSpeed ?? 0 },
  { id: 'progress_stat_perception', name: 'All-Seeing',
    description: 'Allocate {X} points into Perception',
    tiers: [10, 25, 50], category: 'Progression',
    getValue: (s) => (s as unknown as { _statPerception?: number })._statPerception ?? 0 },
  { id: 'progress_stat_energy', name: 'Live Wire',
    description: 'Allocate {X} points into Energy',
    tiers: [10, 25, 50], category: 'Progression',
    getValue: (s) => (s as unknown as { _statEnergy?: number })._statEnergy ?? 0 },
  { id: 'progress_stat_shield', name: 'Bulwark',
    description: 'Allocate {X} points into Shield',
    tiers: [10, 25, 50], category: 'Progression',
    getValue: (s) => (s as unknown as { _statShield?: number })._statShield ?? 0 },
  { id: 'progress_stat_structure', name: 'Ironframe',
    description: 'Allocate {X} points into Structure',
    tiers: [10, 25, 50], category: 'Progression',
    getValue: (s) => (s as unknown as { _statStructure?: number })._statStructure ?? 0 },
  { id: 'progress_points_spent', name: 'Specialist',
    description: 'Spend {X} total stat points',
    tiers: [10, 50, 150, 400, 1000], tierNames: BRONZE_DIAMOND,
    category: 'Progression',
    getValue: (s) => (s as unknown as { _statSum?: number })._statSum ?? 0 },
  { id: 'progress_max_single_stat', name: 'Singular Focus',
    description: 'Push any single stat to 100',
    tiers: [100], category: 'Progression',
    getValue: (s) => (s as unknown as { _statMax?: number })._statMax ?? 0 },
  { id: 'progress_balanced_build', name: 'Well-Rounded',
    description: 'Reach 20+ in every core stat',
    tiers: [20], category: 'Progression',
    getValue: (s) => (s as unknown as { _statMin?: number })._statMin ?? 0 },
  { id: 'progress_total_xp', name: 'Veteran',
    description: 'Earn {X} cumulative XP',
    tiers: [1000, 10000, 50000, 250000, 1000000], tierNames: BRONZE_DIAMOND,
    category: 'Progression',
    getValue: (s) => (s as unknown as { _totalXp?: number })._totalXp ?? 0 },
  { id: 'progress_all_doors', name: 'Four Winds',
    description: 'Discover all four doors (N/S/E/W) in a single map',
    tiers: [1], category: 'Exploration',
    getValue: (s) => (s as unknown as { _allDoors?: number })._allDoors ?? 0 },
  { id: 'progress_building_diversity', name: 'Master Planner',
    description: 'Have one of each of the 9 building types active',
    tiers: [9], category: 'Progression',
    getValue: (s) => (s as unknown as { _buildingDiversity?: number })._buildingDiversity ?? 0 },
  { id: 'progress_building_maxed', name: 'Pinnacle',
    description: 'Upgrade {X} buildings to their maximum level',
    tiers: [1, 3, 9], category: 'Progression',
    getValue: (s) => (s as unknown as { _buildingsMaxed?: number })._buildingsMaxed ?? 0 },

  // ══════════════════════════════════════════════════════
  // LOOT & EQUIPMENT (26)
  // ══════════════════════════════════════════════════════
  { id: 'loot_basic_count', name: 'Bargain Bin',
    description: 'Loot {X} basic items (score 1–30)',
    tiers: [10, 50, 250, 1000, 5000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootBasicLooted },
  { id: 'loot_uncommon_count', name: 'Above Average',
    description: 'Loot {X} uncommon items (score 31–60)',
    tiers: [10, 50, 250, 1000, 5000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootUncommonLooted },
  { id: 'loot_rare_count', name: 'Connoisseur',
    description: 'Loot {X} rare items (score 61–85)',
    tiers: [5, 25, 100, 500, 2000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootRareLooted },
  { id: 'loot_epic_count', name: 'Epic Hoarder',
    description: 'Loot {X} epic items (score 86–95)',
    tiers: [1, 10, 50, 250, 1000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootEpicLooted },
  { id: 'loot_legendary_count', name: 'Lucky Find',
    description: 'Loot {X} legendary items (score 96+)',
    tiers: [1, 5, 25, 100, 500], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootLegendaryLooted },
  { id: 'loot_total_count', name: 'Pack Rat',
    description: 'Collect {X} items in total',
    tiers: [25, 250, 1500, 7500, 25000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.lootTotalLooted },
  { id: 'loot_single_run_record', name: 'Overflowing Cargo',
    description: 'Collect {X} items in a single run',
    tiers: [10, 30, 75, 150],
    category: 'Loot', getValue: (s) => s.bestRunLootCount },
  { id: 'weapons_unique_discovered', name: 'Arsenal Cataloguer',
    description: 'Discover {X} unique weapon types',
    tiers: [5, 15, 30, 40],
    category: 'Loot', getValue: (s) => s.weaponsUniqueDiscovered.size },
  { id: 'weapons_one_of_each_category', name: 'Five-Tool Operator',
    description: 'Discover at least one weapon of each category',
    tiers: [5], category: 'Loot',
    getValue: (s) => (s as unknown as { _categoriesDiscovered?: number })._categoriesDiscovered ?? 0 },
  { id: 'weapons_full_tier_progression', name: 'Tiers of Joy',
    description: 'Discover at least one weapon of every tier 1–10',
    tiers: [10], category: 'Loot',
    getValue: (s) => (s as unknown as { _tiersDiscovered?: number })._tiersDiscovered ?? 0 },
  { id: 'mythic_first_forged', name: 'Mythic Awakening',
    description: 'Forge your first mythic weapon (tier 6+)',
    tiers: [1], category: 'Loot', getValue: (s) => s.forgeWeaponsForged >= 1 ? 1 : 0 },
  { id: 'mythic_all_categories_t10', name: 'Pantheon',
    description: 'Own a tier-10 weapon of each of the 5 categories simultaneously',
    tiers: [5], category: 'Loot', getValue: (s) => s.tier10CategoriesOwnedNow },
  { id: 'mythic_one_per_category', name: 'Mythos Complete',
    description: 'Have ever owned a tier-10 weapon in each of the 5 categories',
    tiers: [5], category: 'Loot', getValue: (s) => s.tier10CategoriesEverOwned.size },
  { id: 'forge_weapons_total', name: 'The Smith',
    description: 'Forge {X} weapons at the base',
    tiers: [1, 10, 50, 200, 1000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.forgeWeaponsForged },
  { id: 'forge_tier10_total', name: 'Apex Forger',
    description: 'Forge {X} tier-10 weapons',
    tiers: [1, 5, 25, 100],
    category: 'Loot', getValue: (s) => s.forgeTier10Forged },
  { id: 'forge_mythic_full_set', name: 'Forge of Legends',
    description: 'Personally forge a tier-10 weapon in each of the 5 categories',
    tiers: [5], category: 'Loot', getValue: (s) => s.forgeTier10Categories.size },
  { id: 'equip_all_slots_filled', name: 'Battle-Ready',
    description: 'Equip every upgrade slot',
    tiers: [5], category: 'Loot', getValue: (s) => s.equipSlotsFilledMax },
  { id: 'equip_full_epic_loadout', name: 'Epic Ensemble',
    description: 'Equip epic-or-better items in all 5 upgrade slots',
    tiers: [5], category: 'Loot', getValue: (s) => s.equipEpicSlotsMax },
  { id: 'equip_full_legendary_loadout', name: 'God Roll',
    description: 'Equip legendary items in all 5 upgrade slots',
    tiers: [5], category: 'Loot', getValue: (s) => s.equipLegendarySlotsMax },
  { id: 'loot_perfect_score', name: 'Perfect Roll',
    description: 'Loot an item with score 100',
    tiers: [1], category: 'Loot', getValue: (s) => s.score100Count },
  { id: 'equip_score_one_used', name: 'Trash Tier Champion',
    description: 'Equip and use a score-1 item in combat for at least one floor',
    tiers: [1], category: 'Loot', getValue: (s) => s.equipScore1FloorsCleared },
  { id: 'run_multi_legendary', name: 'Jackpot Run',
    description: 'Loot {X} legendary items in a single run',
    tiers: [2, 5, 10], category: 'Loot', getValue: (s) => s.bestRunLegendaryCount },
  { id: 'loot_boss_high_score', name: 'Boss Tribute',
    description: 'Loot a score-95+ item from a boss',
    tiers: [1], category: 'Loot', getValue: (s) => s.bossDropAtLeast95Count },
  { id: 'trader_lifetime_sold_value', name: 'Salvage Mogul',
    description: 'Earn {X} money by selling loot to the trader',
    tiers: [500, 5000, 25000, 100000, 500000], tierNames: BRONZE_DIAMOND,
    category: 'Loot', getValue: (s) => s.moneyFromSales },
  { id: 'trader_single_sale_record', name: 'Big Ticket',
    description: 'Sell a single item for {X} money',
    tiers: [100, 500, 2000, 10000],
    category: 'Loot', getValue: (s) => s.bestSinglePrice },
  { id: 'weapons_full_collection', name: 'Completionist Arsenal',
    description: 'Discover every weapon in the game',
    tiers: [40], // replaced at init time with WEAPON_DEFS.length if available
    category: 'Loot', getValue: (s) => s.weaponsUniqueDiscovered.size },

  // ══════════════════════════════════════════════════════
  // CHALLENGE / META / MISC (27)
  // ══════════════════════════════════════════════════════
  { id: 'challenge_no_damage_run', name: 'Untouchable',
    description: 'Complete {X} deployments without taking damage',
    tiers: [1, 5, 20], category: 'Challenge', getValue: (s) => s.noDamageRuns },
  { id: 'challenge_low_hp_survivor', name: 'Hanging by a Wire',
    description: 'End {X} deployments with under 10% structure',
    tiers: [1, 5, 15], category: 'Challenge', getValue: (s) => s.lowHpClears },
  { id: 'challenge_recall_under_fire', name: 'Tactical Retreat',
    description: 'Successfully recall while in combat {X} times',
    tiers: [1, 10, 50], category: 'Challenge', getValue: (s) => s.combatRecalls },
  { id: 'challenge_swarm_wave_survived', name: 'Tide Breaker',
    description: 'Survive {X} full swarm waves',
    tiers: [1, 5, 25], category: 'Challenge', getValue: (s) => s.fullSwarmsSurvived },
  { id: 'speed_map_clear', name: 'Blitz Operator',
    description: 'Clear a map in under {X} seconds',
    tiers: [480, 300, 180], minTracker: true,
    category: 'Challenge', getValue: (s) => s.fastestMapClearSec,
    formatValue: formatDuration },
  { id: 'speed_boss_kill', name: 'Decapitation Strike',
    description: 'Kill a boss within 30 seconds {X} times',
    tiers: [1, 5, 25], category: 'Challenge', getValue: (s) => s.fastBossKills },
  { id: 'speed_level_10', name: 'Rapid Ascent',
    description: 'Reach pilot level 10 within {X} total runs',
    tiers: [20, 10, 5], minTracker: true,
    category: 'Challenge', getValue: (s) => s.runsToLevel10 },
  { id: 'restrict_melee_only', name: 'Knuckle Sandwich',
    description: 'Complete {X} deployments with only melee weapons',
    tiers: [1, 5, 20], category: 'Challenge', getValue: (s) => s.meleeOnlyRuns },
  { id: 'restrict_solo_category', name: 'Pure Build',
    description: 'Complete {X} runs using exactly one weapon category',
    tiers: [1, 5, 25], category: 'Challenge',
    getValue: (s) => sumCategory(s.soloCategoryClears) },
  { id: 'restrict_no_upgrades', name: 'Stock Chassis',
    description: 'Complete {X} runs without spending stat points or buying upgrades',
    tiers: [1, 3, 10], category: 'Challenge', getValue: (s) => s.noUpgradeRuns },
  { id: 'variety_all_categories_run', name: 'Jack of All Tools',
    description: 'Use a weapon from each category in {X} single runs',
    tiers: [1, 10, 50], category: 'Challenge', getValue: (s) => s.fiveCategoryRuns },
  { id: 'variety_loadout_swap', name: 'Field Engineer',
    description: 'Swap your full weapon loadout mid-deployment {X} times',
    tiers: [1, 10, 50], category: 'Challenge', getValue: (s) => s.midRunLoadoutSwaps },
  { id: 'meta_hours_played', name: 'Time Investment',
    description: 'Play for {X}',
    tiers: [3600, 18000, 90000, 360000],
    category: 'Meta', getValue: (s) => s.secondsPlayed,
    formatValue: formatDuration },
  { id: 'meta_deployments', name: 'Veteran Pilot',
    description: 'Launch {X} total deployments',
    tiers: [10, 100, 500, 2000],
    category: 'Meta', getValue: (s) => s.totalDeployments },
  { id: 'meta_sessions', name: 'Returning Operator',
    description: 'Start {X} distinct game sessions',
    tiers: [5, 25, 100], category: 'Meta', getValue: (s) => s.totalSessions },
  { id: 'meta_play_days', name: 'Daily Driver',
    description: 'Play on {X} different calendar days',
    tiers: [3, 14, 30], category: 'Meta', getValue: (s) => s.uniquePlayDays.size },
  { id: 'trader_trades_done', name: 'Regular Customer',
    description: 'Complete {X} trades with the trader',
    tiers: [10, 50, 250], category: 'Meta', getValue: (s) => s.tradesCompleted },
  { id: 'trader_legendary_bought', name: 'High Roller',
    description: 'Purchase {X} top-tier items (score 90+) from the trader',
    tiers: [1, 5, 20], category: 'Meta', getValue: (s) => s.legendaryBought },
  { id: 'death_first', name: 'It Happens to Everyone',
    description: 'Die for the first time',
    tiers: [1], category: 'Misc', getValue: (s) => s.totalDeaths },
  { id: 'death_total', name: 'Scrap Heap',
    description: 'Lose {X} deployments',
    tiers: [10, 100, 500], category: 'Misc', getValue: (s) => s.totalDeaths },
  { id: 'death_comeback', name: 'Phoenix Protocol',
    description: 'Win {X} runs after dropping below 10% HP',
    tiers: [1, 10, 50], category: 'Misc', getValue: (s) => s.comebackRuns },
  { id: 'fun_self_bomb', name: 'Hoist by Own Petard',
    description: 'Kill yourself with your own bomb',
    tiers: [1], hidden: true,
    category: 'Misc', getValue: (s) => s.selfBombDeaths },
  { id: 'fun_pacifist_explore', name: "Cartographer's Oath",
    description: 'Reach 100% map exploration without a single kill',
    tiers: [1, 3, 10],
    category: 'Misc', getValue: (s) => s.pacifistExplorations },
  { id: 'fun_stand_still', name: 'Statue Mode',
    description: 'Stand completely still for 60 seconds in one deployment',
    tiers: [1], hidden: true,
    category: 'Misc', getValue: (s) => s.standStillTriggered },
  { id: 'fun_status_smorgasbord', name: 'Walking Hazard',
    description: 'Be afflicted by burn, slow, AND stun simultaneously',
    tiers: [1, 10], hidden: true,
    category: 'Misc', getValue: (s) => s.allStatusSimultaneous },
  { id: 'meta_category_complete_challenge', name: 'Trial Master',
    description: 'Unlock every Challenge achievement',
    tiers: [1], category: 'Meta',
    getValue: (s) => (s as unknown as { _challengeCompleted?: number })._challengeCompleted ?? 0 },
  { id: 'meta_collector', name: 'Completionist',
    description: 'Unlock {X}% of all achievements',
    tiers: [75, 90, 100], category: 'Meta',
    getValue: (s) => (s as unknown as { _percentUnlocked?: number })._percentUnlocked ?? 0 },
];
